import React, { Component } from "react";
import T from "prop-types";
import { fromJS } from "immutable";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { dollars } from "LIB/formatters/currency";
import DropDown from "MODULES/shared/components/DropDown";
import {
  Authorized,
  DataTable,
  DropDownItem,
  Icon,
  Forbidden,
  LocationFlag,
  NoteTooltip as IconTooltip,
  PartNumber
} from "MODULES/shared/components";
import { useCurrentUser } from "APP_ROOT/hooks";
import {
  WAREHOUSE,
  CREATE_COC,
  QUALITY_CONTROL,
  QUALITY_CONTROL_MANAGER
} from "MODULES/users/constants";
import { condition, EXCHANGE_SALE } from "MODULES/SalesOrders/constants";
import { OUT_ON_EXCHANGE, QC_HOLD } from "MODULES/inventories/constants";
import {
  feeTableHeader,
  displayCustRmaWarning,
  isLastExchange,
  ExchangeIcon
} from "MODULES/SalesOrders/helpers";
import { properCase } from "LIB/formatters/text";
import CancelConsignment from "MODULES/SalesOrders/components/Detail/Body/LineItems/CancelConsignment/CancelConsignment";
import QcPreapproved from "MODULES/SalesOrders/components/Detail/Body/LineItems/QcPreapproved/QcPreapproved";
import ConvertToExchangeModal from "MODULES/SalesOrders/components/Detail/Body/LineItems/ConvertToExchange/ConvertToExchangeModal";
import ConvertToExchange from "MODULES/SalesOrders/components/Detail/Body/LineItems/ConvertToExchange/ConvertToExchange";
import CreateSecondExchangeModal from "MODULES/SalesOrders/components/Detail/Body/LineItems/CreateSecondExchange/CreateSecondExchangeModal";
import ConditionalCreateSecondExchange from "APP_ROOT/modules/SalesOrders/components/Detail/Body/LineItems/CreateSecondExchange/ConditionalCreateSecondExchange";
import ReleaseExportComplianceHold from "MODULES/SalesOrders/components/Detail/Body/LineItems/ReleaseExportComplianceHold/ReleaseExportComplianceHold";
import { NoteTooltip } from "MODULES/shared/components/NoteTooltip";
import { isEmpty, get } from "lodash";
import SendQualityControlDocuments from "MODULES/SalesOrders/components/Detail/Body/LineItems/SendQualityControlDocuments/SendQualityControlDocuments";
import RequestPreauthorization from "MODULES/SalesOrders/components/Detail/Body/LineItems/RequestPreauthorization/RequestPreauthorization";
import SendQualityControlDocumentsModal from "MODULES/SalesOrders/components/Detail/Body/LineItems/SendQualityControlDocuments/SendQualityControlDocumentsModal";
import { RmaRequest } from "./RmaRequest";
import Edit from "./Edit";
import ExchangeReversal from "./ExchangeReversal";
import Cancel from "./Cancel";
import DownloadCoC from "./DownloadCoC";
import DownloadExchangeAgreement from "./DownloadExchangeAgreement";
import SendExchangeAgreement from "./SendExchangeAgreement";
import InternalOutrightSale, {
  InternalOutrightSaleModal
} from "./InternalOutrightSale";
import OutrightSale, { OutrightSaleModal } from "./OutrightSale";
import CancelOutrightSale from "./CancelOutrightSale";
import CancelSubsequentExchange from "./CancelSubsequentExchange";
import CancelledControls from "./CancelledControls";

export const cancelledStatus = ({ cancelled }) =>
  classNames({ "danger-table-cap": cancelled });

const WarningsIcons = ({ lineItem }) => {
  const reserveForSale = get(lineItem, "inventory.brokered_sale", false);
  const hasHazmat = lineItem.hazmat;
  const hasContainerFee =
    lineItem.container_fee && parseFloat(lineItem.container_fee) > 0;

  if (!hasHazmat && !hasContainerFee && !reserveForSale) return <td />;

  return (
    <td>
      <div style={{ display: "flex", alignItems: "center" }}>
        {hasHazmat && (
          <IconTooltip
            icon="fa-warning"
            placement="top"
            note="Hazmat Fee"
            color="#f44336"
            style={{ marginRight: "5px" }} // Add space between the two icons if needed
          />
        )}
        {hasContainerFee && (
          <IconTooltip
            icon="fa-dollar"
            placement="top"
            note="Container Fee"
            color="#2196F3"
          />
        )}
        {reserveForSale && (
          <IconTooltip
            icon="fa-bolt"
            placement="top"
            note="Brokered"
            color="#4787F8"
          />
        )}
      </div>
    </td>
  );
};

WarningsIcons.propTypes = {
  lineItem: T.shape({
    hazmat: T.bool.isRequired,
    container_fee: T.oneOfType([T.string, T.number])
  }).isRequired
};

const ShippedIcon = ({
  converted_from_exchange: convertedFromExchange,
  quantity,
  quantity_shipped: quantityShipped
}) => {
  if (quantity === quantityShipped) {
    return <Icon name="truck" />;
  }
  if (convertedFromExchange) {
    return <Icon name="undo" />;
  }
  return null;
};

ShippedIcon.propTypes = {
  converted_from_exchange: T.bool,
  quantity: T.number,
  quantity_shipped: T.number
};

const ExchangeSaleIcon = ({ lineItem, className }) => {
  if (!lineItem.exchange_sale) return <td className={className} />;
  return (
    <td className={className}>
      <IconTooltip
        icon="fa-exchange"
        placement="top"
        note={
          lineItem.flat_exchange
            ? "Flat Exchange Sale"
            : properCase(EXCHANGE_SALE)
        }
        color="#FFFFFF"
      />
    </td>
  );
};

ExchangeSaleIcon.propTypes = {
  lineItem: T.shape({
    exchange_sale: T.bool.isRequired
  }).isRequired,
  className: T.string
};

const TruncatedTableCell = ({ string, size = 20 }) => {
  if (!string || (string && string.length <= size)) return <td>{string}</td>;
  return <td>{`${string.slice(0, size)}...`}</td>;
};

TruncatedTableCell.propTypes = {
  string: T.string.isRequired,
  size: T.number
};

const OPEN_DOWNLOAD_COC = "openDownloadCoC";
const OPEN_OUTRIGHT = "openOutright";
const OPEN_INTERNAL_OUTRIGHT = "openInternalOutright";
const OPEN_SEND_QC_DOCS = "openSendQcDocs";
const OPEN_CONVERT_TO_EXCHANGE = "openConvertToExchange";
const OPEN_CREATE_SECOND_EXCHANGE = "openCreateSecondExchange";

function renderRow(
  lineItem,
  open,
  index,
  openRma,
  openOutright,
  openInternalOutright,
  openSendQcDocs,
  closeOutright,
  setModalType,
  rowIcon,
  company
) {
  const { cancelled, credit_exchange: creditExchange } = lineItem;
  const rowClasses = classNames("clickable", {
    "text-muted": cancelled || creditExchange
  });
  const isExchange = lineItem.exchange_sale;

  const lastExchange = isLastExchange(lineItem);

  const canRequestRma =
    lineItem.shipped && (!isExchange || (isExchange && lastExchange));

  const displayOutrightSale =
    !lineItem.inventory.brokered_exchange &&
    ((!lineItem.outright_sales_item_id &&
      lastExchange &&
      lineItem.inventory.status === OUT_ON_EXCHANGE) ||
      (lineItem.most_recent_repair_item &&
        lastExchange &&
        lineItem.most_recent_repair_item.exchange_gone_outright));

  const currentUser = useCurrentUser();
  const condAbbrev = condition(lineItem.condition).abbrev;

  return (
    <tr key={lineItem.id} className={rowClasses} onClick={() => open(lineItem)}>
      <td>{index + 1}</td>
      <td className={cancelledStatus(lineItem)} />
      <WarningsIcons lineItem={lineItem} /> {/* Consolidated warnings */}
      {rowIcon && <td>{rowIcon}</td>}
      {displayCustRmaWarning(lineItem) ? (
        <td>
          <IconTooltip
            icon="fa-cog"
            placement="top"
            note="Cust. RMA"
            color="#FFFFFF"
          />
        </td>
      ) : (
        <td />
      )}
      <td>
        <PartNumber part={lineItem.part} />
      </td>
      <td>
        {lineItem.inventory.placeholder ? (
          lineItem.serial_number
        ) : (
          <Link
            to={`/inventories/${lineItem.inventory.uuid ||
              lineItem.inventory_id}`}
          >
            {lineItem.serial_number}
          </Link>
        )}
      </td>
      <td>
        {lineItem.sell_as_alt &&
          (lineItem.sell_as_alt.alternate_part_id ? (
            <PartNumber
              part={{
                id: lineItem.sell_as_alt.alternate_part_id,
                part_number: lineItem.sell_as_alt.name
              }}
            />
          ) : (
            lineItem.sell_as_alt.name
          ))}
      </td>
      <td className="text-center">
        <LocationFlag
          location={lineItem.inventory.store_owner_id}
          consignment_type={lineItem.inventory.consignment_type}
          consignedDate={lineItem.inventory.consigned_date}
          inventory={lineItem.inventory}
        />
      </td>
      <td className="text-center">
        <LocationFlag
          location={lineItem.inventory.store_location_id}
          transferStatus={lineItem.inventory.transfer_status}
        />
      </td>
      <TruncatedTableCell string={lineItem.location} size={13} />
      <TruncatedTableCell string={lineItem.traced_to} />
      <TruncatedTableCell string={lineItem.tagged_by} size={14} />
      <td>{lineItem.outgoing_awb_number}</td>
      <td>{condAbbrev}</td>
      <td className="text-center">
        <ExchangeIcon lineItem={lineItem} />
      </td>
      <td className="text-right">{lineItem.quantity}</td>
      <td className="text-right">{lineItem.quantity_shipped}</td>
      <td className="text-right">
        {dollars(
          lineItem.exchange_sale
            ? lineItem.exchange_fee
            : lineItem.price_per_unit
        )}
      </td>
      <td>
        {(!isEmpty(lineItem.internal_note) ||
          !isEmpty(lineItem.external_note)) && (
          <NoteTooltip
            note={[lineItem.internal_note, lineItem.external_note]
              .filter(n => n !== null && n !== "")
              .join(" | ")}
            className={classNames({ "link-muted": lineItem.cancelled })}
          />
        )}
      </td>
      <td
        className="text-center td-shrink-icon"
        onClick={e => e.stopPropagation()}
      >
        {!cancelled ? (
          <DropDown
            icon="fa fa-fw fa-ellipsis-v"
            menuClass="dropdown-menu-right large"
          >
            {/* Sort of insane that I have to do this to appease prop-types */}
            <React.Fragment>
              {isExchange && (
                <React.Fragment>
                  <SendExchangeAgreement
                    onClick={open}
                    lineItem={lineItem}
                    company={company}
                  />
                  <DownloadExchangeAgreement lineItem={lineItem} />
                </React.Fragment>
              )}
              <RequestPreauthorization salesItem={lineItem} />
              {canRequestRma && (
                <RmaRequest lineItem={lineItem} openRma={openRma} />
              )}
              {!lineItem.shipped && <Cancel lineItem={lineItem} />}
              <InternalOutrightSale
                lineItem={lineItem}
                open={openInternalOutright}
              />
              {displayOutrightSale && (
                <OutrightSale
                  lineItem={lineItem}
                  open={openOutright}
                  close={closeOutright}
                />
              )}

              <ExchangeReversal lineItem={lineItem} />
              <ConvertToExchange
                lineItem={lineItem}
                open={() => setModalType(OPEN_CONVERT_TO_EXCHANGE, lineItem)}
              />
              <CancelOutrightSale lineItem={lineItem} />
              <CancelSubsequentExchange lineItem={lineItem} />
              <CancelConsignment lineItem={lineItem} />
              <ConditionalCreateSecondExchange
                lineItem={lineItem}
                open={() => setModalType(OPEN_CREATE_SECOND_EXCHANGE, lineItem)}
                currentUser={currentUser}
              />
              <DropDownItem
                disabled={lineItem.qc_status === QC_HOLD}
                onClick={() => setModalType(OPEN_DOWNLOAD_COC, lineItem)}
              >
                <Authorized
                  permissions={[CREATE_COC]}
                  roles={[QUALITY_CONTROL]}
                  subroles={[QUALITY_CONTROL_MANAGER]}
                >
                  <Icon name="download" fixed />
                  <span className="with-padding">Download CoC</span>
                </Authorized>
              </DropDownItem>
              <QcPreapproved lineItem={lineItem} />
              <ReleaseExportComplianceHold lineItem={lineItem} />
              <SendQualityControlDocuments
                lineItem={lineItem}
                open={openSendQcDocs}
              />
            </React.Fragment>
          </DropDown>
        ) : (
          <CancelledControls lineItem={lineItem} />
        )}
      </td>
    </tr>
  );
}

export default class Table extends Component {
  state = {
    open: false,
    selectedItem: {},
    modalType: null,
    openOutright: false,
    openInternalOutright: false
  };

  open = lineItem => this.setState({ open: true, selectedItem: lineItem });

  close = () => this.setState({ open: false });

  openModalType = (type, lineItem) =>
    this.setState({ [type]: true, selectedItem: lineItem, modalType: type });

  closeModalType = type => this.setState({ [type]: false, modalType: null });

  render() {
    const {
      company,
      results,
      isLoading,
      openRma,
      rowIcon,
      salesOrder
    } = this.props;
    const {
      selectedItem,
      open,
      openOutright,
      openInternalOutright,
      modalType
    } = this.state;

    const salesEAOrExchFeeColHeader = feeTableHeader(results);
    const currentUser = useCurrentUser();

    return (
      <div>
        <Forbidden roles={[WAREHOUSE]}>
          <Edit
            editing={open}
            close={this.close}
            salesOrder={salesOrder}
            lineItem={fromJS(selectedItem)}
          />
        </Forbidden>
        <DataTable.Mini loading={isLoading} numbered noMargin>
          <DataTable.Head>
            <th />
            {rowIcon && <th />}
            <th />
            <th />
            <th />
            <th>Part #</th>
            <th>Serial #</th>
            <th>Sell As Alt</th>
            <th className="text-center">Store Owner</th>
            <th className="text-center">Store Loc.</th>
            <th>Loc.</th>
            <th>Trace To</th>
            <th>Tagged By</th>
            <th>Outgoing AWB #</th>
            <th>Cond.</th>
            <th className="text-center">Exch</th>
            <th className="text-right">QTY</th>
            <th className="text-right">Shipped</th>
            <th className="text-right">{salesEAOrExchFeeColHeader}</th>
            <th className="text-center" />
          </DataTable.Head>
          <DataTable.Body>
            {results.map((lineItem, index) =>
              renderRow(
                lineItem,
                this.open,
                index,
                openRma,
                item => this.openModalType(OPEN_OUTRIGHT, item),
                item => this.openModalType(OPEN_INTERNAL_OUTRIGHT, item),
                item => this.openModalType(OPEN_SEND_QC_DOCS, item),
                () => this.closeModalType(OPEN_OUTRIGHT),
                (type, item) => this.openModalType(type, item),
                rowIcon,
                company
              )
            )}
          </DataTable.Body>
        </DataTable.Mini>
        <OutrightSaleModal
          close={() => this.closeModalType(OPEN_OUTRIGHT)}
          visible={openOutright}
          lineItem={selectedItem}
        />
        <InternalOutrightSaleModal
          close={() => this.closeModalType(OPEN_INTERNAL_OUTRIGHT)}
          visible={openInternalOutright}
          lineItem={selectedItem}
        />
        <DownloadCoC
          open={modalType === OPEN_DOWNLOAD_COC}
          close={() => this.closeModalType(OPEN_DOWNLOAD_COC)}
          salesItem={selectedItem}
          salesOrder={salesOrder}
        />
        <ConvertToExchangeModal
          open={modalType === OPEN_CONVERT_TO_EXCHANGE}
          close={() => this.closeModalType(OPEN_CONVERT_TO_EXCHANGE)}
          lineItem={selectedItem}
          salesOrder={salesOrder}
        />
        <CreateSecondExchangeModal
          lineItem={selectedItem}
          open={modalType === OPEN_CREATE_SECOND_EXCHANGE}
          close={() => this.closeModalType(OPEN_CREATE_SECOND_EXCHANGE)}
          currentUser={currentUser}
        />
        <SendQualityControlDocumentsModal
          lineItem={selectedItem}
          open={modalType === OPEN_SEND_QC_DOCS}
          close={() => this.closeModalType(OPEN_SEND_QC_DOCS)}
          salesOrder={salesOrder}
        />
      </div>
    );
  }
}

Table.propTypes = {
  results: T.arrayOf(T.shape({})).isRequired,
  isLoading: T.bool.isRequired,
  openRma: T.func.isRequired,
  rowIcon: T.string,
  company: T.shape({
    name: T.string.isRequired,
    blanket_exchange_agreement: T.bool.isRequired
  }).isRequired,
  salesOrder: T.shape({})
};



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Detail/Body/LineItems/Table.jsx